/**
 * LSATDiagnosticModal
 *
 * Shown when a new LSAT (or SAT) learner opens their domain for the first
 * time — detected by calling GET /adaptive/mastery/{learnerId} and checking
 * whether all skills are at the cold_start tier (or the list is empty).
 *
 * Flow:
 *   1. "Start with a Diagnostic Test?" prompt
 *   2. Yes → POST /adaptive/cat-session {domain, mode: "diagnostic", max_items: 10}
 *   3. After completion, show mastery summary
 *   4. No  → dismiss (learner starts curriculum normally)
 *
 * Props:
 *   domain      — string  ("lsat" | "sat")
 *   learnerId   — string
 *   onConfirm   — () => void  (called when diagnostic session starts — caller navigates)
 *   onDecline   — () => void  (called when learner skips diagnostic)
 *   sessionData — object|null (populated by parent after POST returns; triggers summary view)
 *   masteryData — object[]    (mastery records, used to compute summary message)
 */

import React, { useState } from 'react';
import { adaptiveAPI } from '../../services/api';

const DOMAIN_LABELS = { lsat: 'LSAT', sat: 'SAT' };
const SECTION_LABELS = {
  lsat: 'Logical Reasoning',
  sat:  'Math & Evidence-Based Reading',
};

export default function LSATDiagnosticModal({
  domain,
  learnerId,
  onConfirm,
  onDecline,
  masteryData = [],
}) {
  const [phase, setPhase] = useState('prompt');   // 'prompt' | 'loading' | 'summary' | 'error'
  const [summary, setSummary] = useState(null);
  const [errorMsg, setErrorMsg] = useState(null);

  const domainLabel = DOMAIN_LABELS[domain] || domain.toUpperCase();
  const sectionLabel = SECTION_LABELS[domain] || 'Core Skills';

  const handleYes = async () => {
    setPhase('loading');
    try {
      const { data } = await adaptiveAPI.startCatSession({
        domain,
        mode: 'diagnostic',
        max_items: 10,
        learner_id: learnerId,
      });
      setSummary(data);
      setPhase('summary');
      if (onConfirm) onConfirm(data);
    } catch (e) {
      setErrorMsg(
        e?.response?.data?.detail ||
        'Could not start the diagnostic. You can begin the curriculum directly.',
      );
      setPhase('error');
    }
  };

  const handleNo = () => {
    if (onDecline) onDecline();
  };

  // Compute mastery percentage for summary
  const masteryPct = (() => {
    if (!summary?.mastery_estimate) return null;
    const est = summary.mastery_estimate;
    if (typeof est === 'number') return Math.round(est * 100);
    if (typeof est?.score === 'number') return Math.round(est.score * 100);
    return null;
  })();

  const recommendedStart = summary?.recommended_start_skill
    || summary?.recommended_next
    || null;

  return (
    <div style={backdropStyle} role="dialog" aria-modal="true" aria-label={`${domainLabel} diagnostic`}>
      <div style={modalStyle}>

        {/* ── Prompt phase ── */}
        {phase === 'prompt' && (
          <>
            <div style={iconRowStyle}>
              <span style={{ fontSize: 36 }}>📋</span>
            </div>
            <h2 style={titleStyle}>Start with a Diagnostic Test?</h2>
            <p style={bodyStyle}>
              A short 10-question adaptive diagnostic will pinpoint exactly where
              to begin in the <strong>{domainLabel}</strong> curriculum — saving
              you from reviewing material you already know.
            </p>
            <ul style={bulletListStyle}>
              <li>~10 minutes</li>
              <li>Personalized starting point</li>
              <li>No score impact — pure placement</li>
            </ul>
            <div style={btnRowStyle}>
              <button style={primaryBtnStyle} onClick={handleYes}>
                Yes, run the diagnostic
              </button>
              <button style={secondaryBtnStyle} onClick={handleNo}>
                No, start from the beginning
              </button>
            </div>
          </>
        )}

        {/* ── Loading phase ── */}
        {phase === 'loading' && (
          <div style={centeredStyle}>
            <div style={spinnerStyle} />
            <p style={{ color: '#94a3b8', marginTop: 16 }}>
              Starting your adaptive diagnostic…
            </p>
          </div>
        )}

        {/* ── Summary phase ── */}
        {phase === 'summary' && (
          <>
            <div style={iconRowStyle}>
              <span style={{ fontSize: 36 }}>✅</span>
            </div>
            <h2 style={titleStyle}>Diagnostic Complete</h2>
            {masteryPct !== null ? (
              <p style={bodyStyle}>
                You're at <strong style={{ color: '#22c55e', fontSize: 18 }}>{masteryPct}%</strong>{' '}
                mastery in <strong>{sectionLabel}</strong>.
              </p>
            ) : (
              <p style={bodyStyle}>
                Your diagnostic is complete. We've calibrated your starting point.
              </p>
            )}
            {recommendedStart && (
              <div style={recommendationBoxStyle}>
                <span style={{ fontSize: 13, color: '#94a3b8' }}>Recommended starting point:</span>
                <span style={{ fontWeight: 700, color: '#f8fafc', marginTop: 4 }}>
                  {recommendedStart}
                </span>
              </div>
            )}
            <button style={primaryBtnStyle} onClick={handleNo}>
              Start Learning →
            </button>
          </>
        )}

        {/* ── Error phase ── */}
        {phase === 'error' && (
          <>
            <div style={iconRowStyle}><span style={{ fontSize: 36 }}>⚠️</span></div>
            <h2 style={titleStyle}>Couldn't Start Diagnostic</h2>
            <p style={{ ...bodyStyle, color: '#fca5a5' }}>{errorMsg}</p>
            <button style={primaryBtnStyle} onClick={handleNo}>
              Start from the Beginning
            </button>
          </>
        )}
      </div>
    </div>
  );
}

// ─── Styles ────────────────────────────────────────────────────────────────────

const backdropStyle = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,0.75)',
  backdropFilter: 'blur(6px)',
  zIndex: 300,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 16,
};

const modalStyle = {
  background: '#1e293b',
  border: '1px solid #334155',
  borderRadius: 16,
  padding: '32px 28px',
  maxWidth: 480,
  width: '100%',
  boxShadow: '0 12px 48px rgba(0,0,0,0.5)',
  display: 'flex',
  flexDirection: 'column',
  gap: 12,
};

const iconRowStyle = {
  textAlign: 'center',
};

const titleStyle = {
  fontSize: 22,
  fontWeight: 700,
  color: '#f8fafc',
  margin: 0,
  textAlign: 'center',
};

const bodyStyle = {
  fontSize: 14,
  color: '#cbd5e1',
  lineHeight: 1.65,
  margin: 0,
  textAlign: 'center',
};

const bulletListStyle = {
  listStyle: 'none',
  padding: 0,
  margin: '4px 0',
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
  alignItems: 'center',
  color: '#94a3b8',
  fontSize: 13,
};

const btnRowStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: 10,
  marginTop: 8,
};

const primaryBtnStyle = {
  padding: '12px 24px',
  background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
  color: '#fff',
  border: 'none',
  borderRadius: 10,
  fontSize: 15,
  fontWeight: 700,
  cursor: 'pointer',
  width: '100%',
};

const secondaryBtnStyle = {
  padding: '10px 24px',
  background: 'transparent',
  color: '#94a3b8',
  border: '1px solid #334155',
  borderRadius: 10,
  fontSize: 14,
  cursor: 'pointer',
  width: '100%',
};

const centeredStyle = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: '32px 0',
};

const spinnerStyle = {
  width: 36,
  height: 36,
  border: '3px solid #334155',
  borderTop: '3px solid #6366f1',
  borderRadius: '50%',
  animation: 'spin 0.8s linear infinite',
};

const recommendationBoxStyle = {
  background: 'rgba(99,102,241,0.1)',
  border: '1px solid rgba(99,102,241,0.3)',
  borderRadius: 10,
  padding: '12px 16px',
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
  textAlign: 'center',
};
